import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Gate for the admin console. Ported from grant_management, trimmed to the org
 * admin case. Unauthenticated callers are bounced to `/login` with the attempted
 * location saved so verify can return them.
 */
const ProtectedRoute: React.FC<{
  children: React.ReactNode;
  requireOrgAdmin?: boolean;
  requireSuperuser?: boolean;
  requirePartner?: boolean;
}> = ({ children, requireOrgAdmin = false, requireSuperuser = false, requirePartner = false }) => {
  const { user, loading, isOrgAdmin, isSuperuser, isPartner } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="protected-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requireSuperuser && !isSuperuser) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4 m-4" data-testid="protected-denied">
        <div className="text-red-800">
          Access denied. This page is only available to SGOKit platform staff.
        </div>
      </div>
    );
  }

  if (requirePartner && !isPartner) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4 m-4" data-testid="protected-denied">
        <div className="text-red-800">
          Access denied. This page is only available to partner school accounts.
        </div>
      </div>
    );
  }

  if (requireOrgAdmin && !isOrgAdmin && !isSuperuser) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4 m-4" data-testid="protected-denied">
        <div className="text-red-800">
          Access denied. This page is only available to organization administrators.
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
